import { Box, Typography } from '@mui/material';
import type { SetResult } from '../liveMatchTypes.ts';

export interface ReplayScoreBoardProps {
  homeTeamName: string;
  awayTeamName: string;
  homePoints: number;
  awayPoints: number;
  setsWon: { home: number; away: number };
  completedSets: SetResult[];
  currentSet: number;
  servingTeam: string | null;
  strategy?: string | null;
  swapped?: boolean;
}

export default function ReplayScoreBoard({
  homeTeamName, awayTeamName,
  homePoints, awayPoints,
  setsWon, completedSets, currentSet,
  servingTeam, strategy, swapped = false,
}: ReplayScoreBoardProps) {
  const setsToWin  = strategy === 'BEST_OF_5' ? 3 : 2;
  const leftIsHome = !swapped;

  const leftName   = leftIsHome ? homeTeamName : awayTeamName;
  const rightName  = leftIsHome ? awayTeamName : homeTeamName;
  const leftScore  = leftIsHome ? homePoints   : awayPoints;
  const rightScore = leftIsHome ? awayPoints   : homePoints;
  const leftWon    = leftIsHome ? setsWon.home : setsWon.away;
  const rightWon   = leftIsHome ? setsWon.away : setsWon.home;
  const leftColor  = leftIsHome ? '#4fc3f7' : '#ef9a9a';
  const rightColor = leftIsHome ? '#ef9a9a' : '#4fc3f7';
  const leftServing  = servingTeam === (leftIsHome  ? 'HOME' : 'AWAY');
  const rightServing = servingTeam === (leftIsHome  ? 'AWAY' : 'HOME');

  return (
    <Box
      sx={{
        bgcolor: '#1a1a2e',
        color: 'white',
        px: 2,
        pt: 1.5,
        pb: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 1.4fr 1fr',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
      }}
    >
      {/* SINISTRA */}
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5 }}>
          {leftServing && <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>🏐</Typography>}
          <Typography noWrap sx={{ fontWeight: 700, opacity: 0.9, fontSize: { xs: '0.85rem', sm: '1.1rem', md: '1.4rem' } }}>
            {leftName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.25, mt: 0.5 }}>
          {Array.from({ length: setsToWin }, (_, i) => (
            <Box key={i} sx={{ width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 }, borderRadius: '50%', bgcolor: i < leftWon ? leftColor : 'rgba(255,255,255,0.18)' }} />
          ))}
        </Box>
      </Box>

      {/* PUNTEGGIO */}
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
          <Typography
            key={`L${leftScore}`}
            sx={{ fontSize: 'clamp(3rem, 12vw, 14rem)', fontWeight: 900, lineHeight: 0.9, color: '#FFD700', fontVariantNumeric: 'tabular-nums', minWidth: '2ch', textAlign: 'right' }}
          >
            {leftScore}
          </Typography>
          <Typography sx={{ fontSize: 'clamp(1.5rem, 5vw, 7rem)', fontWeight: 300, lineHeight: 0.9, color: 'rgba(255,255,255,0.3)', mx: { xs: 0.5, sm: 1 } }}>
            –
          </Typography>
          <Typography
            key={`R${rightScore}`}
            sx={{ fontSize: 'clamp(3rem, 12vw, 14rem)', fontWeight: 900, lineHeight: 0.9, color: '#FFD700', fontVariantNumeric: 'tabular-nums', minWidth: '2ch', textAlign: 'left' }}
          >
            {rightScore}
          </Typography>
        </Box>

        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.45, fontSize: { xs: 10, sm: 12 } }}>
            Set {currentSet}
          </Typography>
          {completedSets.map((s) => {
            const lS = leftIsHome ? s.homePoints : s.awayPoints;
            const rS = leftIsHome ? s.awayPoints : s.homePoints;
            return (
              <Typography key={s.setNumber} variant="caption" sx={{ opacity: 0.6, fontSize: { xs: 10, sm: 12 } }}>
                <span style={{ color: lS > rS ? leftColor : undefined }}>{lS}</span>
                {'-'}
                <span style={{ color: rS > lS ? rightColor : undefined }}>{rS}</span>
              </Typography>
            );
          })}
        </Box>
      </Box>

      {/* DESTRA */}
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5 }}>
          <Typography noWrap sx={{ fontWeight: 700, opacity: 0.9, fontSize: { xs: '0.85rem', sm: '1.1rem', md: '1.4rem' } }}>
            {rightName}
          </Typography>
          {rightServing && <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>🏐</Typography>}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.25, mt: 0.5 }}>
          {Array.from({ length: setsToWin }, (_, i) => (
            <Box key={i} sx={{ width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 }, borderRadius: '50%', bgcolor: i < rightWon ? rightColor : 'rgba(255,255,255,0.18)' }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
